import { capitalize } from "common/utils/capitalize";
import type { Dispatch } from "react";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import cx from "ui/cx";
import Checkbox from "ui/forms/Checkbox";
import IconTriangleDownArrow from "ui/icons/IconTriangleDownArrow";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./TreeSelect.module.scss";
import { initializeTreeState, type TreeAction, treeReducer } from "./treeSelectReducer";
import type { TreeSelectNodeType, TreeSelectProps } from "./treeSelectTypes";

/**
 * TreeSelect behavior:
 * - Parent:
 *    - false → true (select all)
 *    - true → false (unselect all).
 *    - Indeterminate is derived-only; clicking indeterminate acts as false.
 * - Leaf: false ↔ true.
 * - Ancestors derive state from descendants.
 */
export default function TreeSelect({
  treeData,
  defaultExpandedIds = [],
  onSelect,
  onSelectionChange,
  onLeafSelectionChange,
  dataLabel = "items",
}: TreeSelectProps) {
  const [treeState, dispatch] = useReducer(treeReducer, treeData, initializeTreeState);

  // Store latest callbacks in refs to avoid stale closures without causing infinite loops
  const onSelectionChangeRef = useRef(onSelectionChange);
  const onLeafSelectionChangeRef = useRef(onLeafSelectionChange);

  // Update refs on every render to always have latest callbacks
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
    onLeafSelectionChangeRef.current = onLeafSelectionChange;
  });

  const { selectedCount, initialExpandedFromSelection, rootChecked, selectableNodeCount } = useMemo(() => {
    const selectedCountLocal = countSelectedNodes(treeState);
    const totalCountLocal = countTotalNodes(treeState);
    const initialExpandedFromSelectionLocal = getExpandedIdsFromSelection(treeState);
    const allNonDisabledSelectedLocal = totalCountLocal > 0 && areAllNonDisabledNodesSelected(treeState);
    let rootCheckedLocal: boolean | "indeterminate";
    if (allNonDisabledSelectedLocal) {
      rootCheckedLocal = true;
    } else if (selectedCountLocal > 0) {
      rootCheckedLocal = "indeterminate";
    } else {
      rootCheckedLocal = false;
    }
    return {
      selectedCount: selectedCountLocal,
      initialExpandedFromSelection: initialExpandedFromSelectionLocal,
      rootChecked: rootCheckedLocal,
      selectableNodeCount: totalCountLocal,
    };
  }, [treeState]);

  const [openNodeIds, setOpenNodeIds] = useState<string[]>(() => {
    const merged = new Set<string>([...defaultExpandedIds, ...initialExpandedFromSelection]);
    return Array.from(merged);
  });

  function countSelectedNodes(nodes: TreeSelectNodeType[]): number {
    return nodes.reduce((count, node) => {
      // Skip disabled nodes in the count
      if (node.disabled) {
        return count + (node.children.length > 0 ? countSelectedNodes(node.children) : 0);
      }
      let total = node.selected === true || node.selected === "indeterminate" ? count + 1 : count;
      if (node.children.length > 0) {
        total += countSelectedNodes(node.children);
      }
      return total;
    }, 0);
  }

  function countTotalNodes(nodes: TreeSelectNodeType[]): number {
    return nodes.reduce((total, node) => {
      // Skip disabled nodes in the count
      if (node.disabled) {
        return total + (node.children ? countTotalNodes(node.children) : 0);
      }
      return total + 1 + (node.children ? countTotalNodes(node.children) : 0);
    }, 0);
  }

  function collectSelectedIds(nodes: TreeSelectNodeType[]): string[] {
    const out: string[] = [];
    nodes.forEach((n) => {
      if (n.selected === true || n.selected === "indeterminate") {
        out.push(n.id);
      }
      if (n.children.length) {
        out.push(...collectSelectedIds(n.children));
      }
    });
    return out;
  }

  function collectMinimumSelectedIds(nodes: TreeSelectNodeType[]): string[] {
    const out: string[] = [];
    nodes.forEach((node) => {
      if (node.selected === true || node.selected === "indeterminate") {
        const hasSelectedChildren = node.children.some(
          (child) => child.selected === true || child.selected === "indeterminate",
        );
        if (hasSelectedChildren) {
          //recurse and don't include this node
          out.push(...collectMinimumSelectedIds(node.children));
        } else {
          out.push(node.id);
        }
      }
    });
    return out;
  }

  // Call callbacks when treeState changes, using refs to always get latest version
  useEffect(() => {
    onSelectionChangeRef.current?.(collectSelectedIds(treeState));
    onLeafSelectionChangeRef.current?.(collectMinimumSelectedIds(treeState));
  }, [treeState]);

  const handleRootToggle = (_checked: boolean | "indeterminate"): void => {
    if (selectableNodeCount === 0) {
      return;
    }
    let next: boolean | "indeterminate";
    if (rootChecked === false) {
      next = true;
    } else if (rootChecked === true) {
      next = false;
    } else {
      // indeterminate -> select all (not toggle back to false like before)
      next = true;
    }
    dispatch({ type: "TOGGLE_ALL", checked: next });
    if (next === true) {
      const idsToOpen = new Set<string>(openNodeIds);
      collectAllNodeIds(treeState, idsToOpen);
      setOpenNodeIds(Array.from(idsToOpen));
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <span className={styles.count}>
          {selectedCount} {dataLabel} selected
        </span>
        <Checkbox
          checked={rootChecked}
          onCheckedChange={handleRootToggle}
          disabled={selectableNodeCount === 0}
          label={`All ${capitalize(dataLabel)}`}
          className={styles.checkbox}
          labelClassName={styles.checkboxLabel}
        />
      </div>
      <div className={styles.tree}>
        {treeState?.length ? (
          treeState.map((node) => (
            <TreeSelectItem
              key={node.id}
              node={node}
              openNodeIds={openNodeIds}
              setOpenNodeIds={setOpenNodeIds}
              level={0}
              dispatch={dispatch}
              onSelect={onSelect}
            />
          ))
        ) : (
          <p>No items available</p>
        )}
      </div>
    </div>
  );
}

interface TreeSelectItemProps {
  node: TreeSelectNodeType;
  openNodeIds: string[];
  setOpenNodeIds: (ids: string[]) => void;
  level: number;
  dispatch: Dispatch<TreeAction>;
  onSelect?: (id: string, checked: boolean | "indeterminate") => void;
}

/**
 * Recursive item component for rendering tree nodes with checkbox and expansion.
 */
function TreeSelectItem({ node, openNodeIds, setOpenNodeIds, level, dispatch, onSelect }: TreeSelectItemProps) {
  const { id, label, children = [], selected, icon, disabled = false, disabledTooltip } = node;
  const isExpanded = openNodeIds.includes(id);
  const hasChildren = children.length > 0;

  const handleToggle = () => {
    setOpenNodeIds(isExpanded ? openNodeIds.filter((openId) => openId !== id) : [...openNodeIds, id]);
  };

  const hasSelectableChildren = hasChildren && children.some((c) => !c.disabled);

  const handleSelect = () => {
    let nextChecked: boolean | "indeterminate";

    if (!hasSelectableChildren) {
      nextChecked = selected !== true; // leaf (or all children disabled): toggle boolean
    } else if (selected === false) {
      nextChecked = true; // parent: false -> true (select all descendants)
    } else if (selected === true) {
      nextChecked = "indeterminate"; // parent: true -> indeterminate (parent-only selection; children cleared in reducer)
    } else {
      nextChecked = false; // parent: indeterminate -> false (unselect parent)
    }

    dispatch({ type: "TOGGLE_NODE", id, checked: nextChecked });
    if (hasChildren && nextChecked === true) {
      const idsToOpen = new Set<string>(openNodeIds);
      idsToOpen.add(id);
      collectDescendantIds(children, idsToOpen);
      setOpenNodeIds(Array.from(idsToOpen));
    }
    onSelect?.(id, nextChecked);
  };

  const labelContent = (
    <div className={styles.labelWrapper}>
      <span className={`${styles.labelIcon} ${!selected ? styles.subdued : ""}`}>{icon}</span>
      <span className={`${styles.labelText} ${!selected ? styles.subdued : ""}`}>{label}</span>
    </div>
  );

  return (
    <div>
      <div className={cx(styles.row, disabled && "disabled")}>
        {hasChildren ? (
          <button className={styles.chevron} onClick={handleToggle}>
            <IconTriangleDownArrow size={8} rotate={isExpanded ? 0 : -90} />
          </button>
        ) : (
          <span className={cx(styles.chevron, styles.placeholder)}>
            <IconTriangleDownArrow size={8} rotate={isExpanded ? 0 : -90} />
          </span>
        )}
        <Checkbox
          checked={selected}
          onCheckedChange={handleSelect}
          disabled={disabled}
          label={disabled && disabledTooltip ? wrapWithTooltip(labelContent, disabledTooltip) : labelContent}
        />
      </div>
      {isExpanded && hasChildren && (
        <div className={styles.children}>
          {children.map((child) => (
            <TreeSelectItem
              key={child.id}
              node={child}
              openNodeIds={openNodeIds}
              setOpenNodeIds={setOpenNodeIds}
              level={level + 1}
              dispatch={dispatch}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function areAllNonDisabledNodesSelected(nodes: TreeSelectNodeType[]): boolean {
  return nodes.every((node) => {
    // Disabled nodes pass if they have no children or if all non-disabled descendants are selected
    if (node.disabled) {
      return node.children.length === 0 || areAllNonDisabledNodesSelected(node.children);
    }

    if (node.children.length > 0) {
      const hasNonDisabledChildren = node.children.some((child) => !child.disabled);
      // If a node's children are all disabled, resolve based on the parent's own selected/indeterminate state
      if (!hasNonDisabledChildren) {
        return node.selected === true || node.selected === "indeterminate";
      }
      // Nodes with non-disabled children must have all non-disabled children selected (deep check) and the parent is considered selected/indeterminate
      if (!areAllNonDisabledNodesSelected(node.children)) {
        return false;
      }
    }

    return node.selected === true || node.selected === "indeterminate";
  });
}

function getExpandedIdsFromSelection(nodes: TreeSelectNodeType[], acc: Set<string> = new Set()): string[] {
  nodes.forEach((node) => {
    if (node.selected === true || node.selected === "indeterminate") {
      acc.add(node.id);
    }
    if (node.children.length > 0) {
      getExpandedIdsFromSelection(node.children, acc);
    }
  });
  return Array.from(acc);
}

function collectDescendantIds(children: TreeSelectNodeType[], acc: Set<string>): void {
  children.forEach((child) => {
    acc.add(child.id);
    if (child.children.length > 0) {
      collectDescendantIds(child.children, acc);
    }
  });
}

function collectAllNodeIds(nodes: TreeSelectNodeType[], acc: Set<string>): void {
  nodes.forEach((node) => {
    acc.add(node.id);
    if (node.children.length > 0) {
      collectDescendantIds(node.children, acc);
    }
  });
}
